import type {
  AgentObject as AgentObjectBase,
  AuthChallengeResponse,
  AuthVerifyResponse,
  OperatorObject,
  RegisterAgentRequest,
  RegisterAgentResponse,
} from "@altheia-xyz/types";

// F13: backend now derives is_pending + null'd registered_tx_signature from
// the row. The base type in @altheia-xyz/types@0.0.1 doesn't have these yet
// (they ship in 0.0.2). Augment until republish.
// 2026-05-11: also surface revoked_at + revoked_reason so the recovery banner
// can show "Revoked X ago · reason" instead of generic copy.
// 2026-06-10: chain field — Sui dispatch + sui-side on-chain IDs.
export type Chain = "solana" | "sui";
export type AgentObject = AgentObjectBase & {
  is_pending?: boolean;
  registered_tx_signature?: string | null;
  revoked_at?: string | null;
  revoked_reason?: string | null;
  chain?: Chain;
  sui_vault_id?: string | null;
  sui_policy_id?: string | null;
  sui_agent_cap_id?: string | null;
};

export interface UpdateOperatorRequest {
  display_name?: string | null;
  email?: string | null;
  alert_webhook_url?: string | null;
  alert_slack_webhook?: string | null;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3001";

export interface RegisterAgentApiResponse extends RegisterAgentResponse {
  transaction: { base64: string; fee_payer?: string; swig_ensured_created?: boolean };
  /**
   * Backend mints an apiKey per agent on registration. Cleartext is returned
   * exactly ONCE — operator must copy it now, we only retain the hash.
   * Used by the SDK as `Authorization: Bearer <cleartext>` on /sdk/agent_check.
   */
  api_key: { cleartext: string; prefix: string };
}

interface RequestInitWithToken extends Omit<RequestInit, "body"> {
  token?: string;
  body?: BodyInit | Record<string, unknown> | null;
}

async function request<T>(path: string, init: RequestInitWithToken = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.token) headers["Authorization"] = `Bearer ${init.token}`;

  const body =
    init.body === null || init.body === undefined
      ? undefined
      : typeof init.body === "string" || init.body instanceof FormData || init.body instanceof Blob
        ? (init.body as BodyInit)
        : JSON.stringify(init.body);

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    body,
    credentials: "include",
  });
  if (!res.ok && res.status !== 202) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }
  return res.json() as Promise<T>;
}

export interface CreateSwigFirstResponse {
  status: "create_swig_first";
  message: string;
  swig_account: string;
  transaction: { base64: string; fee_payer?: string };
}

export interface SuiRegisterRequest {
  chain: "sui";
  name: string;
  framework: "eliza" | "virtuals" | "griffain" | "sak" | "mcp" | "custom";
  model_commitment?: string;
  agent_id: string;
  sui_vault_id: string;
  sui_policy_id: string;
  sui_agent_cap_id: string;
  agent_address: string;
  register_digest?: string;
  policy?: Record<string, unknown>;
}

export interface SuiAuditRecord {
  agent_id: string;
  decision: "allowed" | "denied" | "info";
  event_type: string;
  action_type?: string;
  amount?: number;
  asset?: string;
  target?: string;
  reason_code?: string | null;
  reason_detail?: string | null;
  tx_digest?: string | null;
}

export interface SuiRegisterApiResponse {
  agent: AgentObject;
  api_key: { cleartext: string; prefix: string };
}

export const api = {
  authChallenge: (wallet: string) =>
    request<AuthChallengeResponse>("/auth/challenge", {
      method: "POST",
      body: { wallet },
    }),
  authVerify: (input: { wallet: string; challenge: string; signature: string }) =>
    request<AuthVerifyResponse>("/auth/verify", { method: "POST", body: input }),
  // Sui: same endpoint, chain=sui → backend verifies the Sui personal-message
  // signature (Slush/Suiet/Privy embedded) keyed by the Sui address.
  authVerifySui: (input: { wallet: string; challenge: string; signature: string }) =>
    request<AuthVerifyResponse>("/auth/verify", { method: "POST", body: { ...input, chain: "sui" } }),

  getOperatorMe: (token: string) =>
    request<OperatorObject>("/operator/me", { token }),
  updateOperatorMe: (input: UpdateOperatorRequest, token: string) =>
    request<OperatorObject>("/operator/me", {
      method: "PATCH",
      token,
      body: input as Record<string, unknown>,
    }),

  listAgents: (token: string) =>
    request<{ agents: AgentObject[] }>("/agents", { token }),
  getAgent: (id: string, token: string) =>
    request<{ agent: AgentObject }>(`/agents/${id}`, { token }),
  registerAgent: (input: RegisterAgentRequest, token: string) =>
    request<RegisterAgentApiResponse | CreateSwigFirstResponse>("/agents", {
      method: "POST",
      token,
      body: input,
    }),
  // chain=sui: record-only registration. The agent was provisioned on-chain by
  // the @altheia-xyz/sui SDK/CLI; we record the object ids so the audit poller
  // correlates events (agent_id_bytes = hex(utf8(agent_id))) and the dashboard
  // renders it. No backend tx, no Swig. Mirrors SuiRegisterRequestSchema.
  registerSuiAgent: (input: SuiRegisterRequest, token: string) =>
    request<SuiRegisterApiResponse>("/agents", { method: "POST", token, body: input as unknown as Record<string, unknown> }),
  // Record a Sui action to the audit ledger: agent-loop denials (the abort
  // emits nothing on-chain) and owner admin actions (verifiable by tx digest).
  // Allowed agent swaps come from the chain poller, not here.
  recordSuiAudit: (input: SuiAuditRecord, token: string) =>
    request<AuditEvent>("/sui/audit", { method: "POST", token, body: input as unknown as Record<string, unknown> }),
  updateAgent: (
    id: string,
    body: { name?: string; policy?: Record<string, unknown> },
    token: string,
  ) =>
    request<{
      agent_id: string;
      new_policy_commitment?: string;
      transaction: { base64: string } | null;
    }>(`/agents/${id}`, { method: "PATCH", token, body }),
  confirmAgent: (id: string, txSignature: string, token: string) =>
    request<{ agent_id: string; status: string }>(`/agents/${id}/confirm`, {
      method: "POST",
      token,
      body: { tx_signature: txSignature },
    }),
  pauseAgent: (id: string, token: string) =>
    request<{ agent_id: string; transaction: { base64: string } }>(`/agents/${id}/pause`, {
      method: "POST",
      token,
      body: {},
    }),
  unpauseAgent: (id: string, token: string) =>
    request<{ agent_id: string; transaction: { base64: string } }>(`/agents/${id}/unpause`, {
      method: "POST",
      token,
      body: {},
    }),
  archiveAgent: (id: string, token: string) =>
    request<{ agent_id: string; transaction: { base64: string } }>(`/agents/${id}/archive`, {
      method: "POST",
      token,
      body: {},
    }),
  revokeAgent: (
    id: string,
    body: { reason_code: string; reason_detail?: string },
    token: string,
  ) =>
    request<{ agent_id: string; transaction: { base64: string } }>(`/agents/${id}/revoke`, {
      method: "POST",
      token,
      body,
    }),
  confirmRevoke: (id: string, txSignature: string, token: string) =>
    request<{ agent_id: string; status: string }>(`/agents/${id}/revoke/confirm`, {
      method: "POST",
      token,
      body: { tx_signature: txSignature },
    }),
  rotateApiKey: (id: string, token: string) =>
    request<{ api_key: { cleartext: string; prefix: string } }>(
      `/agents/${id}/api-key/rotate`,
      { method: "POST", token, body: {} },
    ),
  cancelPendingAgent: (id: string, token: string) =>
    request<{ cancelled: true; agent_id: string }>(
      `/agents/${id}/cancel`,
      { method: "POST", token, body: {} },
    ),
  // Force a fresh chain → DB sync. Used after pause/unpause/revoke/edit policy
  // when the Helius webhook isn't running (local dev).
  syncAgent: (id: string, token: string) =>
    request<{ agent: AgentObject }>(`/agents/${id}/sync`, {
      method: "POST",
      token,
      body: {},
    }),
  getSmartAccountBalance: (id: string, token: string) =>
    request<{
      smart_account: string;
      sol_lamports: number;
      sol_ui: number;
      tokens: Array<{
        ata: string;
        mint: string;
        raw_amount: string;
        ui_amount: number | null;
        decimals: number;
      }>;
    }>(`/agents/${id}/smart-account-balance`, { token }),
  buildWithdrawTx: (id: string, token: string, recipientPubkey?: string) =>
    request<{
      smart_account: string;
      operator_wallet: string;
      recipient_wallet: string;
      custom_recipient: boolean;
      bundled_close?: boolean;
      transaction: { base64: string; fee_payer: string };
      last_valid_block_height: number;
    }>(`/agents/${id}/withdraw-tx`, {
      method: "POST",
      token,
      body: recipientPubkey ? { recipient_pubkey: recipientPubkey } : {},
    }),
  getAgentAudit: (
    id: string,
    token: string,
    opts: { filter?: "all" | "allowed" | "denied"; limit?: number; before?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.filter) q.set("filter", opts.filter);
    if (opts.limit != null) q.set("limit", String(opts.limit));
    if (opts.before) q.set("before", opts.before);
    const qs = q.toString();
    return request<{ events: AuditEvent[] }>(
      `/agents/${id}/audit${qs ? `?${qs}` : ""}`,
      { token },
    );
  },
  searchAudit: (
    token: string,
    opts: {
      filter?: "all" | "allowed" | "denied";
      limit?: number;
      before?: string;
      agent_id?: string;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.filter) q.set("filter", opts.filter);
    if (opts.limit != null) q.set("limit", String(opts.limit));
    if (opts.before) q.set("before", opts.before);
    if (opts.agent_id) q.set("agent_id", opts.agent_id);
    const qs = q.toString();
    return request<{ events: (AuditEvent & { agent_id: string | null })[] }>(
      `/audit/search${qs ? `?${qs}` : ""}`,
      { token },
    );
  },
};

export interface AuditEvent {
  id: string;
  event_type: string;
  decision: "allowed" | "denied" | "n/a" | null;
  reason_code: string | null;
  reason_detail: string | null;
  action_descriptor: { type?: string; amount?: number; asset?: string; target?: string } | null;
  tx_signature: string | null;
  slot: number | null;
  block_time: string | null;
  amount_token: number | null;
  asset_mint: string | null;
  source: string | null;
  destination: string | null;
  merkle_root_committed: string | null;
  chain?: Chain;
  created_at: string;
}
